import { TaskListFilter, TaskStatus } from "@repo/db/client";

/**
 * Which side of the referral-sent line an option keeps.
 *
 * One tri-state rather than a pair of booleans, so that "drop both" — which
 * would show a permanently empty list — cannot be spelled, and so the axis
 * stays one axis applied in one pass.
 */
export type SentReferralFilter = "any" | "unsent-only" | "sent-only";

/**
 * How each filter option narrows and orders the task list.
 *
 * `ReferralNotSent` and `ReferralSent` are both strict subsets of
 * `ActionRequired` — same status, one keeping each side of the referral-sent
 * line — so nothing is reachable only through either, and together they cover
 * `ActionRequired` exactly. The two filtering axes are applied by different
 * layers: `status` in the tandem store query, `sentReferrals` in memory, since
 * `QueryBuilder` has no join. Neither is applied twice; re-checking status in
 * memory would filter against a store result that is one render stale, briefly
 * emptying the list.
 *
 * `workQueueSort` is a third axis, and unlike the other two it drops nothing —
 * it only reorders. So the layer rule above does not constrain it: the sort may
 * read the same referral-sent set the filter uses without double-applying
 * anything.
 */
type TaskListFilterConfig = {
	/** The status to show, or undefined to show every status. */
	status: TaskStatus | undefined;
	/** Which side of the referral-sent line to keep. */
	sentReferrals: SentReferralFilter;
	/**
	 * Order by the coordinator's working rule — unsent first, then priority,
	 * then oldest referral — instead of by most recent activity.
	 *
	 * True for the views staff pick work up from, where oldest-first means "what
	 * has been waiting longest". False where they review what just happened, and
	 * oldest-first would reverse the list they came to read. That intent is the
	 * discriminator, not whether the first key happens to be dead: it is dead
	 * under both `Completed` and `ReferralSent`, which choose differently.
	 */
	workQueueSort: boolean;
	/**
	 * Whether staff can pick another order from the sort menu, such as least
	 * recently worked first. Offered on the open views only: on Completed
	 * nothing is left to work, and Any Status includes Completed.
	 */
	offersSortChoice: boolean;
};

const TASK_LIST_FILTER_CONFIG: Record<TaskListFilter, TaskListFilterConfig> = {
	[TaskListFilter.AnyStatus]: {
		status: undefined,
		sentReferrals: "any",
		workQueueSort: false,
		offersSortChoice: false,
	},
	[TaskListFilter.ActionRequired]: {
		status: TaskStatus.ActionRequired,
		sentReferrals: "any",
		workQueueSort: true,
		offersSortChoice: true,
	},
	[TaskListFilter.ReferralNotSent]: {
		status: TaskStatus.ActionRequired,
		sentReferrals: "unsent-only",
		workQueueSort: true,
		offersSortChoice: true,
	},
	[TaskListFilter.ReferralSent]: {
		status: TaskStatus.ActionRequired,
		sentReferrals: "sent-only",
		// Every task here is sent, so the working rule's first key is dead and
		// what remains is priority, then oldest referral first. That ordering is
		// the point of this view: the referral sent six weeks ago with nothing
		// back is the one to chase. The opposite call from `Completed`, where
		// staff review recent work and oldest-first would reverse the list.
		workQueueSort: true,
		offersSortChoice: true,
	},
	[TaskListFilter.Snoozed]: {
		status: TaskStatus.Snoozed,
		sentReferrals: "any",
		workQueueSort: false,
		offersSortChoice: true,
	},
	[TaskListFilter.Completed]: {
		status: TaskStatus.Completed,
		sentReferrals: "any",
		workQueueSort: false,
		offersSortChoice: false,
	},
};

/**
 * `users.status_filter` is free text in Postgres and reaches the client both
 * through the sync pull and through the session, neither of which the type
 * system checks. Indexing the config with an unknown value throws mid-render,
 * so parse at those boundaries; falling back is safe for a display preference.
 */
export function parseTaskListFilter(input: unknown): TaskListFilter {
	return Object.values(TaskListFilter).includes(input as TaskListFilter)
		? (input as TaskListFilter)
		: TaskListFilter.ActionRequired;
}

/** The status clause for the store query, or undefined for every status. */
export function statusForTaskListFilter(
	filter: TaskListFilter,
): TaskStatus | undefined {
	return TASK_LIST_FILTER_CONFIG[filter].status;
}

/** Which side of the referral-sent line this option keeps. */
export function sentReferralFilterFor(
	filter: TaskListFilter,
): SentReferralFilter {
	return TASK_LIST_FILTER_CONFIG[filter].sentReferrals;
}

/** Whether to order by the working rule rather than by most recent activity. */
export function sortsByWorkQueue(filter: TaskListFilter): boolean {
	return TASK_LIST_FILTER_CONFIG[filter].workQueueSort;
}

/** Whether staff can pick another order for this view from the sort menu. */
export function offersSortChoice(filter: TaskListFilter): boolean {
	return TASK_LIST_FILTER_CONFIG[filter].offersSortChoice;
}

/**
 * What this view's recommended order does, for the sort menu. Derived from the
 * same config the sort reads, so the two cannot drift: the send-status key
 * only does anything when the view keeps both sides of the sent line.
 */
export function recommendedSortDescription(filter: TaskListFilter): string {
	const { workQueueSort, sentReferrals } = TASK_LIST_FILTER_CONFIG[filter];
	if (!workQueueSort) {
		return "Most recent activity first";
	}
	return sentReferrals === "any"
		? "Unsent first, then priority, then oldest referral"
		: "Priority, then oldest referral";
}
